// src/backendServices/liveStreamManagment/regie_external_sources.ts
//
// La LISTE des sources externes d'une chaîne, pour la sauvegarde du deck : flux HLS
// libres, flux Xtream, caméras RTMP additionnelles, smartphones enregistrés. Elle
// vit côté microservice (propre à la chaîne) ; la sauvegarde en garde une copie, et
// le chargement la redéclare — connectées ou non, la régie retrouve la même liste,
// comme si on venait de les ajouter.
//
// Ce qu'on sauvegarde, et pourquoi :
//   • HLS libre   : libellé + URL (l'URL peut porter un jeton).
//   • Xtream      : serveur, utilisateur, flux — JAMAIS le mot de passe. Au chargement
//                   le compte est réutilisé s'il existe déjà dans le microservice ;
//                   sinon on le signale (mot de passe à ressaisir).
//   • RTMP        : libellé + clé d'origine (le cadreur continue de viser la même URL).
//                   Si la clé est refusée (autre chaîne), nouvelle clé : l'identifiant
//                   de la source change, `idRemap` le dit à l'appelant.
//   • Smartphones : identifiant, nom, mode d'accès (mot de passe OU lien/jeton), rôle
//                   Réalisateur — avec leurs identifiants, à la demande de l'utilisateur.

import {
  listAllHlsStreams, listXtreamAccounts, addHlsStream, setSelectedXtreamChannel,
  listRtmpInputs, addRtmpInput, deleteHlsStream, deleteRtmpInput, deleteXtreamAccount,
} from './api_microservice_sources';
import { fetchRemoteDevices, updateRemoteDevice, RegisteredRemoteDevice } from './api_remoteDevices';
import { RemoteDevice, DeviceAuthMode } from '../../dataTypes/liveStreamManagment/types_webrtc';
import { XtreamCredentials } from '../../dataTypes/liveStreamManagment/sourceRegistry/types';

/** Le microservice fabrique des URL rtmp://hôte:1935/… ; on communique du RTMPS (:1936). */
const enRtmps = (url: string): string => url.replace(/^rtmp:\/\/([^/:]+):1935\//, 'rtmps://$1:1936/');

export interface SavedHls    { sourceId: string; label: string; url: string }
export interface SavedXtream { sourceId: string; label: string; server: string; username: string; streamId: number; icon?: string }
export interface SavedRtmp   { sourceId: string; label: string; key: string }
export interface SavedDevice {
  device_id    : string;
  label        : string;
  auth_mode?   : DeviceAuthMode;
  password?    : string | null;
  access_token?: string | null;
  is_director? : boolean;
}

export interface SavedExternalSources {
  hls    : SavedHls[];
  xtream : SavedXtream[];
  rtmp   : SavedRtmp[];
  devices: SavedDevice[];
}

/**
 * Identifiant d'une source externe dans la régie. UNE seule formule, partagée avec
 * handleAddHlsSource : la recalculer ailleurs, c'est la voir diverger.
 */
export function hlsSourceId(url: string, xtream?: Pick<XtreamCredentials, 'server' | 'username' | 'streamId'>): string {
  return xtream?.streamId
    ? `hls-xtream-${xtream.server.replace(/^https?:\/\//, '').replace(/\//g, '_')}-${xtream.username}-${xtream.streamId}`
    : `hls-custom-${url}`;
}

export interface ExternalSourcesCtx {
  /** `${serveur}/${adresse de la chaîne}/regie` */
  microserviceUrl: string;
  serverUrl      : string;
  streamPath     : string;
}

// ── Lecture ───────────────────────────────────────────────────────────────────

export async function collectExternalSources(ctx: ExternalSourcesCtx): Promise<SavedExternalSources> {
  const base = ctx.microserviceUrl;
  const [{ streams }, { accounts }, devices] = await Promise.all([
    listAllHlsStreams(base),
    listXtreamAccounts(base),
    fetchRemoteDevices(ctx.serverUrl, ctx.streamPath).catch(() => [] as RemoteDevice[]),
  ]);

  const out: SavedExternalSources = { hls: [], xtream: [], rtmp: [], devices: [] };
  for (const s of streams) {
    if (s.type === 'free') {
      out.hls.push({ sourceId: hlsSourceId(s.url), label: s.label, url: s.url });
    } else if (s.type === 'rtmp' && s.rtmp_key) {
      out.rtmp.push({ sourceId: hlsSourceId(s.url), label: s.label, key: s.rtmp_key });
    } else if (s.type === 'xtream' && s.xtream_account_id && s.xtream_stream_id != null) {
      const acc = accounts.find(a => a.id === s.xtream_account_id);
      if (!acc) continue;
      out.xtream.push({
        sourceId: hlsSourceId('', { server: acc.server, username: acc.username, streamId: s.xtream_stream_id }),
        label: s.label, server: acc.server, username: acc.username,
        streamId: s.xtream_stream_id, icon: s.xtream_stream_icon,
      });
    }
  }
  for (const d of devices) {
    out.devices.push({
      device_id: d.device_id, label: d.label, auth_mode: d.auth_mode,
      password: d.password ?? null, access_token: d.access_token ?? null, is_director: !!d.is_director,
    });
  }
  return out;
}

// ── Redéclaration ─────────────────────────────────────────────────────────────

export interface RestoreDeps extends ExternalSourcesCtx {
  /** Façade publique (sert à composer les URL de caméras RTMP). */
  publicBaseUrl  : string;
  existingDevices: RemoteDevice[];
  /** Enregistrement d'un appareil — celui de la régie, qui contrôle le quota. */
  registerDevice : (
    id: string, label: string, password: string, authMode: DeviceAuthMode,
    options?: { silent?: boolean; accessToken?: string },
  ) => Promise<RegisteredRemoteDevice | undefined>;
}

export interface RestoreReport {
  hlsAdded       : number;
  xtreamAdded    : number;
  xtreamMissing  : string[];                              // comptes absents du microservice (mot de passe à ressaisir)
  rtmpAdded      : number;
  rtmpNewKey     : Array<{ label: string; ingestUrl: string }>;
  devicesAdded   : number;
  devicesFailed  : string[];                              // quota atteint ou erreur
}

export async function restoreExternalSources(
  saved: SavedExternalSources, deps: RestoreDeps,
): Promise<{ idRemap: Map<string, string>; report: RestoreReport }> {
  const base = deps.microserviceUrl;
  const idRemap = new Map<string, string>();
  const report: RestoreReport = {
    hlsAdded: 0, xtreamAdded: 0, xtreamMissing: [], rtmpAdded: 0, rtmpNewKey: [], devicesAdded: 0, devicesFailed: [],
  };

  const [{ streams }, { accounts }, { inputs }] = await Promise.all([
    listAllHlsStreams(base), listXtreamAccounts(base), listRtmpInputs(base),
  ]);

  // HLS libres : ignoré s'il existe déjà avec cette URL.
  for (const h of saved.hls ?? []) {
    if (streams.some(s => s.type === 'free' && s.url === h.url)) continue;
    try { await addHlsStream(base, { url: h.url, label: h.label }); report.hlsAdded++; } catch (e) { console.warn('[restauration] HLS', h.label, e); }
  }

  // Xtream : le compte doit exister (pas de mot de passe dans la sauvegarde).
  const norm = (s: string) => s.replace(/\/+$/, '').toLowerCase();
  for (const x of saved.xtream ?? []) {
    const acc = accounts.find(a => norm(a.server) === norm(x.server) && a.username === x.username);
    if (!acc) { report.xtreamMissing.push(`${x.label} (${x.username}@${x.server})`); continue; }
    if (streams.some(s => s.type === 'xtream' && s.xtream_account_id === acc.id && s.xtream_stream_id === x.streamId)) continue;
    try {
      await setSelectedXtreamChannel(base, acc.id, { stream_id: x.streamId, name: x.label, stream_icon: x.icon ?? '' });
      report.xtreamAdded++;
    } catch (e) { console.warn('[restauration] Xtream', x.label, e); }
  }

  // RTMP : on redemande la clé d'origine ; refusée → nouvelle clé, et l'identifiant change.
  for (const r of saved.rtmp ?? []) {
    if (inputs.some(i => i.rtmp_key === r.key)) continue;
    try {
      const { input } = await addRtmpInput(base, { label: r.label, base_url: deps.publicBaseUrl, rtmp_key: r.key });
      report.rtmpAdded++;
      // Un microservice non mis à jour ignore `rtmp_key` et en tire une autre : on le
      // détecte à la clé rendue, pas seulement à l'échec de l'appel.
      if (input.rtmp_key !== r.key) {
        idRemap.set(r.sourceId, hlsSourceId(input.url));
        report.rtmpNewKey.push({ label: r.label, ingestUrl: enRtmps(input.rtmp_ingest_url) });
      }
    } catch {
      try {
        const { input } = await addRtmpInput(base, { label: r.label, base_url: deps.publicBaseUrl });
        report.rtmpAdded++;
        idRemap.set(r.sourceId, hlsSourceId(input.url));
        report.rtmpNewKey.push({ label: r.label, ingestUrl: enRtmps(input.rtmp_ingest_url) });
      } catch (e) { console.warn('[restauration] RTMP', r.label, e); }
    }
  }

  // Smartphones : réenregistrés avec leurs identifiants (mot de passe ou lien).
  for (const d of saved.devices ?? []) {
    if (deps.existingDevices.some(x => x.device_id === d.device_id)) continue;
    try {
      const done = await deps.registerDevice(
        d.device_id, d.label, d.password ?? '', d.auth_mode ?? 'password',
        { silent: true, accessToken: d.access_token ?? undefined },
      );
      if (!done) { report.devicesFailed.push(d.label || d.device_id); continue; }
      report.devicesAdded++;
      if (d.is_director) await updateRemoteDevice(deps.serverUrl, deps.streamPath, done.device_id, { is_director: true }).catch(() => {});
    } catch (e) { report.devicesFailed.push(d.label || d.device_id); console.warn('[restauration] appareil', d.device_id, e); }
  }

  return { idRemap, report };
}

// ── Restauration en attente ───────────────────────────────────────────────────
//
// Les sources externes vivent dans le microservice de la chaîne, qui n'existe que
// tant que la chaîne est ACTIVE (son container est créé à l'activation). Charger une
// sauvegarde sur une chaîne inactive ne peut donc rien redéclarer. Plutôt que de
// perdre la liste, on la garde par chaîne : la régie la rejoue dès que le microservice
// répond (au prochain rechargement des sources, donc à l'activation).
const pendingKey = (uuid: string) => `mediartc_pending_external_sources:${uuid}`;

export function savePendingExternalSources(uuid: string, saved: SavedExternalSources): void {
  try { localStorage.setItem(pendingKey(uuid), JSON.stringify(saved)); } catch {}
}

/** Lit ET retire la restauration en attente de cette chaîne (jamais rejouée deux fois). */
export function takePendingExternalSources(uuid: string | null): SavedExternalSources | null {
  if (!uuid) return null;
  try {
    const raw = localStorage.getItem(pendingKey(uuid));
    if (!raw) return null;
    localStorage.removeItem(pendingKey(uuid));
    return JSON.parse(raw) as SavedExternalSources;
  } catch { return null; }
}

/** Texte d'alerte d'un rapport de restauration, ou '' s'il n'y a rien à dire. */
export function describeRestoreReport(report: RestoreReport): string {
  const notes: string[] = [];
  if (report.xtreamMissing.length) notes.push(`Comptes Xtream à reconfigurer (le mot de passe n'est pas sauvegardé) : ${report.xtreamMissing.join(', ')}.`);
  if (report.rtmpNewKey.length) notes.push(`Caméras RTMP recréées avec une NOUVELLE clé — communiquez la nouvelle URL au cadreur : ${report.rtmpNewKey.map(r => `${r.label} → ${r.ingestUrl}`).join(' ; ')}.`);
  if (report.devicesFailed.length) notes.push(`Appareils non réenregistrés (quota atteint ou erreur) : ${report.devicesFailed.join(', ')}.`);
  return notes.join('\n');
}

// ── Suppression ───────────────────────────────────────────────────────────────

/**
 * Retire du microservice TOUTES les sources externes de la chaîne : flux HLS libres,
 * caméras RTMP (leur clé est perdue), comptes Xtream et leurs flux. Les smartphones
 * se suppriment à part, par la régie (quota, déconnexion). Retourne les identifiants
 * de régie des sources retirées, pour les sortir de la liste et du deck.
 */
export async function clearExternalSources(ctx: ExternalSourcesCtx): Promise<{ removedSourceIds: string[]; failed: number }> {
  const base = ctx.microserviceUrl;
  const [{ streams }, { accounts }] = await Promise.all([listAllHlsStreams(base), listXtreamAccounts(base)]);
  const removedSourceIds: string[] = [];
  let failed = 0;

  for (const s of streams) {
    try {
      if (s.type === 'rtmp')      await deleteRtmpInput(base, s.id);
      else if (s.type === 'free') await deleteHlsStream(base, s.id);
      else continue;                       // Xtream : retiré avec son compte, plus bas
      removedSourceIds.push(hlsSourceId(s.url));
    } catch (e) { failed++; console.warn('[réinitialisation] source', s.label, e); }
  }

  for (const a of accounts) {
    const own = streams.filter(s => s.type === 'xtream' && s.xtream_account_id === a.id);
    try {
      await deleteXtreamAccount(base, a.id);
      own.forEach(s => removedSourceIds.push(hlsSourceId('', { server: a.server, username: a.username, streamId: s.xtream_stream_id! })));
    } catch (e) { failed++; console.warn('[réinitialisation] compte Xtream', a.username, e); }
  }
  return { removedSourceIds, failed };
}
